/**
 * The side-by-side chart of the roofs a proposal offers (ADR 0274).
 *
 * Jonathon, 2026-10-07: "when there's multiple shingle or metal replacement
 * options presented there needs to be an easy comparison chart for the
 * customer after the options are presented and before the upgrades are
 * presented."
 *
 * Nothing here is typed by anybody for the chart. Every tick is something the
 * package already says further up the same document — its Key Differentiator,
 * the shingle or panel its lines name, the upgrades it includes, its wood
 * bonus — so the chart cannot promise what the package does not, and editing
 * a package's words edits its column. He chose that over a list of his own
 * per template (2026-10-07).
 */
import { isWoodBonus } from "./laborOnly.js";
import { shingleLineFor } from "./shingleColors.js";

/** One roof's column. */
export interface ComparedPackage {
  estimateId: string;
  name: string;
  /** The ticks, top to bottom. */
  points: string[];
  /**
   * The ticks the package itself gives, before the rep's changes; present
   * only where the rep changed some, so the preview can show which line is
   * which (ADR 0318).
   */
  packagePoints?: string[];
  totalFormatted: string;
  /** "or about $445/mo", or null when the document carries no payments. */
  monthly: string | null;
  /** The gold "Most popular" badge. At most one column carries it. */
  popular: boolean;
}

/** What the chart is built from, one entry per whole roof on the document. */
export interface ComparisonInput {
  estimateId: string;
  name: string;
  /** The Key Differentiator as the document prints it (already reworded). */
  headline: string | null;
  /** `estimates.roof_type`: "Shingle", "Standing Seam Metal". */
  roofType?: string | null | undefined;
  blocks: readonly {
    heading: string;
    lines: readonly { name: string; body: string | null }[];
  }[];
  /** The upgrades this package includes, by the name the document prints. */
  includedUpgrades: readonly string[];
  /**
   * What the rep changed in this column on the preview,
   * `estimates.compare_edits` (ADR 0318). Null: the column fills itself in
   * from the package, as below.
   */
  edits?: ChartEdits | null | undefined;
  totalFormatted: string;
  monthly: string | null;
}

/**
 * Printed under the chart when any compared roof says it is Class 4.
 * Jonathon's words from the picture he sent, approved 2026-10-07.
 */
export const CLASS_4_INSURANCE_NOTE = {
  lead: "Class 4 shingles can lower your insurance bill.",
  rest: "Many carriers offer a premium discount for impact-resistant roofs — ask your agent.",
} as const;

/**
 * "Key Differentiator: 30+ Year Life Expectancy and 15 Year Workmanship
 * Warranty" → ["30+ Year Life Expectancy", "15 Year Workmanship Warranty"].
 *
 * The line is free text, written two ways in the templates — joined by "and",
 * or by a full stop — and sometimes still carrying the "Key Differentiator:"
 * label it was pasted with. Each part becomes its own tick.
 */
export function differentiatorPoints(headline: string | null): string[] {
  const text = (headline ?? "")
    .trim()
    .replace(/^key differentiator\s*:\s*/i, "");
  return text
    .split(/\s+and\s+|\.\s+|;\s*/i)
    .map((part) =>
      part
        .trim()
        .replace(/[.!]+$/, "")
        .trim(),
    )
    .filter((part) => part !== "");
}

/** "Peace of Mind Wood Preventative Bonus - $1550 Value!" without the "!". */
function woodBonusPoint(input: ComparisonInput): string | null {
  const block = input.blocks.find((each) => isWoodBonus(each.heading));
  if (!block) return null;
  return (
    block.heading
      .trim()
      .replace(/[!.]+$/, "")
      .trim() || null
  );
}

/** "GAF Timberline UHDZ", read from the roof type and the package's lines. */
function productPoint(input: ComparisonInput): string | null {
  const lineNames = input.blocks.flatMap((block) =>
    block.lines.map((line) => line.name),
  );
  return shingleLineFor([input.roofType, ...lineNames])?.shingle ?? null;
}

const mentions = (points: readonly string[], text: string): boolean =>
  points.some((point) => point.toLowerCase().includes(text.toLowerCase()));

/** One column's ticks: what it is, how long, what comes with it. */
export function comparisonPoints(input: ComparisonInput): string[] {
  const points = differentiatorPoints(input.headline);
  const product = productPoint(input);
  // Second, under the life expectancy, as in Jonathon's picture — unless the
  // Key Differentiator already names it.
  if (product && !mentions(points, product)) {
    points.splice(Math.min(1, points.length), 0, product);
  }
  for (const upgrade of input.includedUpgrades) {
    const point = `${upgrade.trim()} Included`;
    if (upgrade.trim() && !mentions(points, point)) points.push(point);
  }
  const bonus = woodBonusPoint(input);
  if (bonus) points.push(bonus);
  return points;
}

/** Whether a package says anywhere that it is Class 4 impact-rated. */
function saysClass4(input: ComparisonInput): boolean {
  const texts = [
    input.headline,
    ...input.blocks.flatMap((block) => [
      block.heading,
      ...block.lines.flatMap((line) => [line.name, line.body]),
    ]),
  ];
  return texts.some((text) => /\bclass\s*(4|iv)\b/i.test(text ?? ""));
}

/**
 * What a rep changed in one roof's column on the preview (ADR 0318).
 *
 * Every change names the package's own line it was made to, so a line the rep
 * did not touch keeps following the package: a different shingle or an
 * upgrade taken off changes it, and only the lines the rep typed stay as
 * typed. Jonathon, 2026-10-30, asked whether a rewritten column should keep
 * following the package for the lines the rep did not touch: "13. b".
 */
export interface ChartEdits {
  /** The package's lines the rep retyped: its words, then the rep's. */
  retyped?: Record<string, string>;
  /** The package's lines the rep took off. */
  removed?: string[];
  /** Lines the rep added, printed after the package's. */
  added?: string[];
}

/**
 * One line of a column on the preview, and what it is made from: one of the
 * package's lines (as the rep retyped it, if they did), or one the rep added.
 */
export type ChartRow =
  | { text: string; from: "package"; line: string }
  | { text: string; from: "added"; index: number };

/**
 * The column's lines on the preview, top to bottom: the package's, each as
 * the rep retyped it and less any they took off, then the rep's own.
 *
 * A retyped line stays when the package no longer says what it replaced: the
 * rep typed it, so it is theirs. It moves to just after the package's lines.
 */
export function chartRows(
  packageLines: readonly string[],
  edits: ChartEdits | null | undefined,
): ChartRow[] {
  const retyped = edits?.retyped ?? {};
  const removed = new Set(edits?.removed ?? []);
  const rows: ChartRow[] = packageLines
    .filter((line) => !removed.has(line))
    .map((line) => ({ text: retyped[line] ?? line, from: "package", line }));
  for (const [line, text] of Object.entries(retyped)) {
    if (!packageLines.includes(line) && !removed.has(line)) {
      rows.push({ text, from: "package", line });
    }
  }
  (edits?.added ?? []).forEach((text, index) =>
    rows.push({ text, from: "added", index }),
  );
  return rows;
}

/** The rep's words for one row, kept as typed until `tidyChartEdits`. */
export function retypeChartRow(
  edits: ChartEdits | null | undefined,
  row: ChartRow,
  text: string,
): ChartEdits {
  if (row.from === "added") {
    const added = [...(edits?.added ?? [])];
    added[row.index] = text;
    return { ...edits, added };
  }
  return { ...edits, retyped: { ...edits?.retyped, [row.line]: text } };
}

/** One row taken off the column. */
export function removeChartRow(
  edits: ChartEdits | null | undefined,
  row: ChartRow,
): ChartEdits | null {
  if (row.from === "added") {
    return tidyChartEdits({
      ...edits,
      added: (edits?.added ?? []).filter((_each, at) => at !== row.index),
    });
  }
  const { [row.line]: _gone, ...retyped } = edits?.retyped ?? {};
  return tidyChartEdits({
    ...edits,
    retyped,
    removed: [...(edits?.removed ?? []), row.line],
  });
}

/** An empty line at the bottom of the column, for the rep to fill in. */
export function addChartRow(edits: ChartEdits | null | undefined): ChartEdits {
  return { ...edits, added: [...(edits?.added ?? []), ""] };
}

/**
 * The changes as they are saved. A package line retyped to its own words is
 * not a change; one retyped to nothing is taken off; an added line left
 * blank is dropped. Null when nothing is left changed, so the column fills
 * itself in again.
 */
export function tidyChartEdits(
  edits: ChartEdits | null | undefined,
): ChartEdits | null {
  const retyped: Record<string, string> = {};
  const removed = new Set(edits?.removed ?? []);
  for (const [line, text] of Object.entries(edits?.retyped ?? {})) {
    const words = text.trim();
    if (words === "") removed.add(line);
    else if (words !== line) retyped[line] = words;
  }
  const added = (edits?.added ?? [])
    .map((text) => text.trim())
    .filter((text) => text !== "");
  const tidy: ChartEdits = {};
  if (Object.keys(retyped).length > 0) tidy.retyped = retyped;
  if (removed.size > 0) tidy.removed = [...removed];
  if (added.length > 0) tidy.added = added;
  return Object.keys(tidy).length > 0 ? tidy : null;
}

/**
 * The ticks a column prints: the package's, with the rep's changes from the
 * preview (ADR 0318). A blank line is dropped rather than printed as an
 * empty tick.
 */
export function columnPoints(input: ComparisonInput): string[] {
  return chartRows(comparisonPoints(input), input.edits)
    .map((row) => row.text.trim())
    .filter((text) => text !== "");
}

/**
 * The columns, and whether the insurance note prints. Null with fewer than two
 * roofs: one roof is a price, not a choice.
 *
 * "Most popular" goes on the first column named Peace of Mind (Jonathon,
 * 2026-10-07) and on nothing when there is none.
 */
export function buildComparison(inputs: readonly ComparisonInput[]): {
  packages: ComparedPackage[];
  class4Note: boolean;
} | null {
  if (inputs.length < 2) return null;
  let popular = false;
  const packages = inputs.map((input) => {
    const isPopular = !popular && /peace\s+of\s+mind/i.test(input.name);
    if (isPopular) popular = true;
    return {
      estimateId: input.estimateId,
      name: input.name,
      points: columnPoints(input),
      ...(tidyChartEdits(input.edits)
        ? { packagePoints: comparisonPoints(input) }
        : {}),
      totalFormatted: input.totalFormatted,
      monthly: input.monthly,
      popular: isPopular,
    };
  });
  return { packages, class4Note: inputs.some(saysClass4) };
}
